<template>
  <div class="monthly-closure">
    <section class="monthly-closure__header">
      <BasicTitle title="Cierre mensual de gastos comunes" />
      <BasicSubtitle
        subtitle="Selecciona el periodo y revisa el preview antes de generar un borrador."
      />
    </section>

    <section class="monthly-closure__steps">
      <button
        v-for="step in steps"
        :key="step.id"
        type="button"
        :class="[
          'monthly-closure__step',
          step.id === selectedStep && 'monthly-closure__step--active',
          isStepDisabled(step.id) && 'monthly-closure__step--disabled',
        ]"
        :disabled="isStepDisabled(step.id)"
        @click="selectStep(step.id)"
      >
        <span>{{ step.id }}</span>
        <p>{{ step.title }}</p>
      </button>
    </section>

    <section
      class="monthly-closure__status"
      :class="`monthly-closure__status--${statusCard.kind}`"
    >
      <div>
        <span>Estado</span>
        <strong>{{ statusCard.title }}</strong>
        <p>{{ statusCard.description }}</p>
      </div>
      <strong v-if="closureId" class="monthly-closure__status-id">
        ID: {{ closureId }}
      </strong>
    </section>

    <section
      v-if="loadedFromHistory"
      class="monthly-closure__history-note"
    >
      <div>
        <strong>Cierre cargado desde historial</strong>
        <p>{{ historyMessage }}</p>
      </div>
      <button type="button" @click="goHistory">Volver al listado</button>
    </section>

    <section
      v-if="warnings.length"
      class="monthly-closure__message monthly-closure__message--warn monthly-closure__global-warning"
    >
      <strong>Warnings detectados: {{ warnings.length }}</strong>
      <ul>
        <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
      </ul>
    </section>

    <section v-if="selectedStep === 1" class="monthly-closure__panel">
      <div class="monthly-closure__form">
        <div class="monthly-closure__field">
          <label class="monthly-closure__label" for="closure-month">Mes</label>
          <Dropdown
            id="closure-month"
            v-model="form.month"
            :options="months"
            optionLabel="name"
            optionValue="id"
            placeholder="Seleccione mes"
            class="monthly-closure__control"
          />
        </div>

        <div class="monthly-closure__field">
          <label class="monthly-closure__label" for="closure-year">Anio</label>
          <InputText
            id="closure-year"
            v-model="form.year"
            type="number"
            class="monthly-closure__control"
          />
        </div>

        <Button
          label="Previsualizar cierre"
          class="monthly-closure__button"
          :disabled="isLoading"
          @click="onPreview"
        />
      </div>

      <div
        v-if="!buildingId"
        class="monthly-closure__message monthly-closure__message--warn"
      >
        Debe seleccionar un edificio para previsualizar el cierre mensual.
      </div>

      <div v-if="isLoading" class="monthly-closure__loading">
        <ProgressSpinner />
        <p>Calculando preview del cierre...</p>
      </div>

      <div
        v-if="errorMessage"
        class="monthly-closure__message monthly-closure__message--error"
      >
        {{ errorMessage }}
      </div>

      <PreviewSummary
        v-if="preview"
        :period-label="periodLabel"
        :items="summaryItems"
      />
    </section>

    <section v-else-if="!preview" class="monthly-closure__panel">
      <EmptyState message="Primero selecciona un periodo y ejecuta el preview." />
    </section>

    <section v-else class="monthly-closure__panel">
      <div class="monthly-closure__review-header">
        <div>
          <h3>{{ currentStep.title }}</h3>
          <p>{{ periodLabel }}</p>
        </div>
        <strong>{{ currentStep.subtitle }}</strong>
      </div>

      <template v-if="selectedStep === 2">
        <ReviewTable
          :columns="incomeColumns"
          :rows="incomeRows"
          empty-message="No hay ingresos en el periodo."
        />
        <TotalBar label="Total de ingresos" :value="money(totals.totalIncome)" />
      </template>

      <template v-if="selectedStep === 3">
        <ReviewTable
          :columns="fineColumns"
          :rows="fineRows"
          empty-message="No hay multas en el periodo."
        />
        <TotalBar label="Total de multas" :value="money(totals.totalFines)" />
      </template>

      <template v-if="selectedStep === 4">
        <ReviewTable
          :columns="expenseColumns"
          :rows="expenseRows"
          empty-message="No hay egresos en el periodo."
        />
        <TotalBar label="Total de egresos" :value="money(totals.totalExpenses)" />
      </template>

      <template v-if="selectedStep === 5">
        <ReviewTable
          :columns="accountColumns"
          :rows="accountRows"
          empty-message="No hay resumen contable disponible."
        />
      </template>

      <template v-if="selectedStep === 6">
        <div class="monthly-closure__cards">
          <article
            v-for="item in calculationItems"
            :key="item.label"
            class="monthly-closure__card"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </article>
        </div>
      </template>

      <template v-if="selectedStep === 7">
        <ReviewTable
          :columns="apartmentColumns"
          :rows="apartmentRows"
          empty-message="No hay liquidaciones por apartamento."
          :on-row-click="selectApartment"
        />
        <div v-if="selectedApartment" class="monthly-closure__concepts">
          <div class="monthly-closure__concepts-header">
            <h4>Conceptos apartamento {{ selectedApartment.apartment }}</h4>
            <button type="button" @click="selectedApartment = null">Cerrar</button>
          </div>
          <ReviewTable
            :columns="conceptColumns"
            :rows="selectedApartment.concepts"
            empty-message="No hay conceptos para este apartamento."
            :compact="true"
          />
        </div>
      </template>

      <template v-if="selectedStep === 8">
        <div class="monthly-closure__cards">
          <article
            v-for="item in buildingSummaryItems"
            :key="item.label"
            class="monthly-closure__card"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </article>
        </div>
        <div v-if="hasGeneratedDraft" class="monthly-closure__success">
          Borrador generado correctamente. ID: {{ closureId }}
        </div>
        <div v-if="hasClosedClosure" class="monthly-closure__success">
          Cierre definitivo confirmado. ID: {{ closureId }}
          <span v-if="closedAtLabel"> Cerrado el {{ closedAtLabel }}.</span>
        </div>
      </template>

      <template v-if="selectedStep === 9">
        <div class="monthly-closure__documents-header">
          <div>
            <h4>Documentos del cierre</h4>
            <p>{{ documentStatusText }}</p>
          </div>
          <Button
            v-if="closureId"
            :label="documentsButtonLabel"
            class="monthly-closure__button"
            :disabled="!canGenerateDocuments"
            @click="onGenerateDocuments"
          />
        </div>

        <div
          v-if="closureId && !hasClosedClosure"
          class="monthly-closure__message monthly-closure__message--warn monthly-closure__message--flat"
        >
          Para generar documentos primero tenes que cerrar definitivamente el mes.
        </div>

        <EmptyState
          v-if="!documentRows.length"
          message="Todavia no se generaron documentos para este cierre."
        />

        <section class="monthly-closure__document-section">
          <h4>Resumen global del edificio</h4>
          <article v-if="buildingDocument" class="monthly-closure__document-card">
            <div>
              <strong>Resumen del edificio</strong>
              <p>{{ dateText(buildingDocument.createdAt) }}</p>
            </div>
            <a
              :href="buildingDocument.fileUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir PDF
            </a>
          </article>
          <EmptyState v-else message="No se encontro PDF global." />
        </section>

        <section class="monthly-closure__document-section">
          <h4>Liquidaciones individuales</h4>
          <div v-if="apartmentDocumentRows.length" class="monthly-closure__table-wrap">
            <table class="monthly-closure__table monthly-closure__table--documents">
              <thead>
                <tr>
                  <th>Apartamento</th>
                  <th>Torre</th>
                  <th>Tipo documento</th>
                  <th>Fecha</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="document in apartmentDocumentRows" :key="document.id">
                  <td>{{ document.apartmentNumber }}</td>
                  <td>{{ document.towerName }}</td>
                  <td>{{ document.typeLabel }}</td>
                  <td>{{ dateText(document.createdAt) }}</td>
                  <td>
                    <a
                      v-if="document.fileUrl"
                      :href="document.fileUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir PDF
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <EmptyState v-else message="No se encontraron PDFs individuales." />
        </section>
      </template>

      <template v-if="selectedStep === 10">
        <div class="monthly-closure__documents-header">
          <div>
            <h4>Envio de liquidaciones</h4>
            <p>{{ sendStatusText }}</p>
          </div>
          <Button
            v-if="closureId"
            :label="sendButtonLabel"
            class="monthly-closure__button"
            :disabled="!canSendDocuments"
            @click="onSendDocuments"
          />
        </div>

        <div
          v-if="!apartmentDocumentRows.length"
          class="monthly-closure__message monthly-closure__message--warn monthly-closure__message--flat"
        >
          Primero tenes que generar los PDFs del cierre.
        </div>

        <div
          v-if="sendWarnings.length"
          class="monthly-closure__message monthly-closure__message--warn monthly-closure__message--flat"
        >
          <strong>Warnings del envio: {{ sendWarnings.length }}</strong>
          <ul>
            <li v-for="warning in sendWarnings" :key="warning">{{ warning }}</li>
          </ul>
        </div>

        <div class="monthly-closure__cards monthly-closure__send-summary">
          <article class="monthly-closure__card">
            <span>Enviados</span>
            <strong>{{ sendSummary.sent }}</strong>
          </article>
          <article class="monthly-closure__card">
            <span>Fallidos</span>
            <strong>{{ sendSummary.failed }}</strong>
          </article>
          <article class="monthly-closure__card">
            <span>Omitidos</span>
            <strong>{{ sendSummary.skipped }}</strong>
          </article>
          <article class="monthly-closure__card">
            <span>PDFs individuales</span>
            <strong>{{ apartmentDocumentRows.length }}</strong>
          </article>
        </div>

        <section class="monthly-closure__document-section">
          <h4>Enviados</h4>
          <div v-if="sentRows.length" class="monthly-closure__table-wrap">
            <table class="monthly-closure__table monthly-closure__table--documents">
              <thead>
                <tr>
                  <th>Apartamento</th>
                  <th>Destinatario</th>
                  <th>Tipo destinatario</th>
                  <th>Email</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in sentRows" :key="item.id">
                  <td>{{ item.apartment }}</td>
                  <td>{{ item.recipient }}</td>
                  <td>{{ item.recipientType }}</td>
                  <td>{{ item.email }}</td>
                  <td>
                    <a
                      v-if="item.fileUrl"
                      :href="item.fileUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir PDF
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <EmptyState v-else message="No hubo envios exitosos." />
        </section>

        <section class="monthly-closure__document-section">
          <h4>Fallidos</h4>
          <div v-if="failedRows.length" class="monthly-closure__table-wrap">
            <table class="monthly-closure__table monthly-closure__table--documents">
              <thead>
                <tr>
                  <th>Apartamento</th>
                  <th>Email</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in failedRows" :key="item.id">
                  <td>{{ item.apartment }}</td>
                  <td>{{ item.email }}</td>
                  <td>{{ item.reason }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <EmptyState v-else message="No hubo envios fallidos." />
        </section>

        <section class="monthly-closure__document-section">
          <h4>Omitidos</h4>
          <div v-if="skippedRows.length" class="monthly-closure__table-wrap">
            <table class="monthly-closure__table monthly-closure__table--documents">
              <thead>
                <tr>
                  <th>Apartamento</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in skippedRows" :key="item.id">
                  <td>{{ item.apartment }}</td>
                  <td>{{ item.reason }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <EmptyState v-else message="No hubo documentos omitidos." />
        </section>
      </template>
    </section>

    <section class="monthly-closure__nav">
      <div v-if="preview" class="monthly-closure__primary-actions">
        <Button
          v-if="!hasGeneratedDraft && !hasClosedClosure"
          :label="generateButtonLabel"
          class="monthly-closure__button"
          :disabled="!canGenerateDraft"
          @click="onGenerateDraft"
        />
        <Button
          v-if="canShowCloseButton"
          :label="closeButtonLabel"
          class="monthly-closure__button monthly-closure__button--danger"
          :disabled="!canCloseClosure"
          @click="onCloseClosure"
        />
        <span
          v-if="hasClosedClosure"
          class="monthly-closure__draft-caption monthly-closure__draft-caption--closed"
        >
          Cierre definitivo confirmado. ID: {{ closureId }}
        </span>
        <span
          v-else-if="hasGeneratedDraft"
          class="monthly-closure__draft-caption"
        >
          Borrador generado. ID: {{ closureId }}
        </span>
      </div>
      <span v-else class="monthly-closure__nav-spacer" />
      <Button
        label="Anterior"
        class="monthly-closure__button monthly-closure__button--secondary"
        :disabled="selectedStep === 1"
        @click="previousStep"
      />
      <Button
        label="Siguiente"
        class="monthly-closure__button"
        :disabled="selectedStep === steps.length || (!preview && selectedStep >= 1)"
        @click="nextStep"
      />
    </section>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  h,
  onBeforeMount,
  reactive,
  ref,
  resolveComponent,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle.vue";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle.vue";
import { actions, currency, dates } from "../../constants";

const monthOptions = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

const steps = [
  { id: 1, title: "Periodo", subtitle: "Seleccion del periodo" },
  { id: 2, title: "Ingresos", subtitle: "Ingresos del periodo" },
  { id: 3, title: "Multas", subtitle: "Multas del periodo" },
  { id: 4, title: "Egresos", subtitle: "Egresos del periodo" },
  { id: 5, title: "Cuentas", subtitle: "Resumen contable" },
  { id: 6, title: "Calculo", subtitle: "Gastos comunes" },
  { id: 7, title: "Apartamentos", subtitle: "Liquidacion por unidad" },
  { id: 8, title: "Edificio", subtitle: "Estado general" },
  { id: 9, title: "Documentos", subtitle: "PDFs del cierre" },
  { id: 10, title: "Envio", subtitle: "Mails de liquidacion" },
];

const getValue = (source, paths, fallback = null) => {
  const list = Array.isArray(paths) ? paths : [paths];
  for (const path of list) {
    const value = String(path)
      .split(".")
      .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), source);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const text = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => currency.convertNumberToCurrency(number(value));

const dateText = (value) => (value ? dates.dateToString(value) : "-");

const normalizeSourceArray = (preview, keys) => {
  for (const key of keys) {
    const value = getValue(preview, key, null);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const normalizeDocumentList = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) return source;

  const documents = source.documents !== undefined ? source.documents : source;
  if (Array.isArray(documents)) return documents;
  if (!documents || typeof documents !== "object") return [];

  const result = [];
  if (documents.buildingSummary) {
    result.push({
      ...documents.buildingSummary,
      documentRole:
        documents.buildingSummary.documentRole ||
        documents.buildingSummary.metadata?.documentRole ||
        "building_summary",
    });
  }

  const apartmentLiquidations = documents.apartmentLiquidations || [];
  if (Array.isArray(apartmentLiquidations)) {
    apartmentLiquidations.forEach((document) => {
      result.push({
        ...document,
        documentRole:
          document.documentRole ||
          document.metadata?.documentRole ||
          "apartment_liquidation",
      });
    });
  }

  return result;
};

const documentRole = (document) => {
  const role = getValue(document, ["documentRole", "metadata.documentRole"], null);
  if (role) return String(role);

  const fileUrl = String(getValue(document, "fileUrl", "")).toLowerCase();
  if (
    fileUrl.includes("resumen") ||
    fileUrl.includes("summary") ||
    fileUrl.includes("building")
  ) {
    return "building_summary";
  }

  if (
    fileUrl.includes("liquidacion") ||
    fileUrl.includes("liquidation") ||
    getValue(document, ["apartmentId", "metadata.apartmentId"], null)
  ) {
    return "apartment_liquidation";
  }

  return "unknown";
};

const normalizeResultArray = (source, keys) => {
  for (const key of keys) {
    const value = getValue(source, key, null);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const ReviewTable = defineComponent({
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, required: true },
    emptyMessage: { type: String, required: true },
    compact: { type: Boolean, default: false },
    onRowClick: { type: Function, default: null },
  },
  setup(props) {
    const DataTable = resolveComponent("DataTable");
    const Column = resolveComponent("Column");

    return () =>
      props.rows.length
        ? h(
            "div",
            {
              class: [
                "monthly-closure__table-wrap",
                props.compact && "monthly-closure__table-wrap--compact",
              ],
            },
            [
              h(
                DataTable,
                {
                  value: props.rows,
                  responsiveLayout: "scroll",
                  breakpoint: "960px",
                  stripedRows: true,
                  removableSort: true,
                  dataKey: "id",
                  paginator: props.rows.length > 10 && !props.compact,
                  rows: 10,
                  class: [
                    "monthly-closure__review-table",
                    props.onRowClick && "monthly-closure__review-table--clickable",
                  ],
                  onRowClick: (event) =>
                    props.onRowClick && props.onRowClick(event.data),
                },
                () =>
                  props.columns.map((column) =>
                    h(
                      Column,
                      {
                        key: column.field,
                        field: column.field,
                        header: column.header,
                        sortable: true,
                        class:
                          column.align === "right"
                            ? "right"
                            : column.center
                            ? "center"
                            : "left",
                      },
                      {
                        body: ({ data }) => text(data[column.field]),
                      }
                    )
                  )
              ),
            ]
          )
        : h(EmptyState, { message: props.emptyMessage });
  },
});

const EmptyState = defineComponent({
  props: {
    message: { type: String, required: true },
  },
  setup(props) {
    return () => h("p", { class: "monthly-closure__empty" }, props.message);
  },
});

const TotalBar = defineComponent({
  props: {
    label: { type: String, required: true },
    value: { type: String, required: true },
  },
  setup(props) {
    return () =>
      h("div", { class: "monthly-closure__total-bar" }, [
        h("span", props.label),
        h("strong", props.value),
      ]);
  },
});

const PreviewSummary = defineComponent({
  props: {
    periodLabel: { type: String, required: true },
    items: { type: Array, required: true },
  },
  setup(props) {
    return () =>
      h("section", { class: "monthly-closure__summary-inline" }, [
        h("div", { class: "monthly-closure__summary-header" }, [
          h("h3", "Resultado del preview"),
          h("p", props.periodLabel),
        ]),
        h(
          "div",
          { class: "monthly-closure__cards" },
          props.items.map((item) =>
            h("article", { key: item.label, class: "monthly-closure__card" }, [
              h("span", item.label),
              h("strong", item.value),
            ])
          )
        ),
      ]);
  },
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    EmptyState,
    PreviewSummary,
    ReviewTable,
    TotalBar,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const defaultPeriod = store.state.monthlyClosure.selectedPeriod;
    const selectedStep = ref(1);
    const selectedApartment = ref(null);
    const generatingDraft = ref(false);
    const closingClosure = ref(false);
    const generatingDocuments = ref(false);
    const sendingDocuments = ref(false);
    const form = reactive({
      month: defaultPeriod.month,
      year: defaultPeriod.year,
    });

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (value && typeof value === "object") return value.id;
      if (value) return value;
      const buildings = store.state.auth.user?.buildings || [];
      return buildings.length === 1 ? buildings[0].id : null;
    });

    const preview = computed(() => store.state.monthlyClosure.preview);
    const routeClosureId = computed(() => route.query.closureId || route.params?.id || null);
    const loadedFromHistory = computed(() => Boolean(routeClosureId.value));
    const currentClosure = computed(() => store.state.monthlyClosure.currentClosure);
    const closureId = computed(
      () =>
        store.state.monthlyClosure.closureId ||
        currentClosure.value?.closureId ||
        currentClosure.value?.id ||
        null
    );
    const closureStatus = computed(
      () =>
        store.state.monthlyClosure.status ||
        currentClosure.value?.status ||
        preview.value?.status ||
        null
    );
    const normalizedClosureStatus = computed(() =>
      String(closureStatus.value || "").toLowerCase()
    );
    const closedAt = computed(
      () =>
        store.state.monthlyClosure.closedAt ||
        currentClosure.value?.closedAt ||
        preview.value?.closedAt ||
        null
    );
    const closedAtLabel = computed(() =>
      closedAt.value ? dateText(closedAt.value) : null
    );
    const totals = computed(() => preview.value?.totals || {});
    const warnings = computed(() => store.state.monthlyClosure.warnings || []);
    const isLoading = computed(() => store.state.monthlyClosure.loading);
    const errorMessage = computed(() => store.state.monthlyClosure.error);
    const hasGeneratedDraft = computed(
      () => Boolean(closureId.value) && normalizedClosureStatus.value === "draft"
    );
    const hasClosedClosure = computed(
      () => Boolean(closureId.value) && normalizedClosureStatus.value === "closed"
    );
    const canGenerateDraft = computed(
      () =>
        Boolean(preview.value) &&
        !isLoading.value &&
        !generatingDraft.value &&
        !hasGeneratedDraft.value &&
        !hasClosedClosure.value
    );
    const canShowCloseButton = computed(
      () => Boolean(closureId.value) && !hasClosedClosure.value
    );
    const canCloseClosure = computed(
      () =>
        Boolean(closureId.value) &&
        hasGeneratedDraft.value &&
        !isLoading.value &&
        !closingClosure.value
    );
    const generateButtonLabel = computed(() =>
      generatingDraft.value ? "Generando borrador..." : "Generar borrador"
    );
    const closeButtonLabel = computed(() =>
      closingClosure.value ? "Cerrando mes..." : "Cerrar mes definitivamente"
    );
    const statusCard = computed(() => {
      if (errorMessage.value) {
        return {
          kind: "error",
          title: "Error",
          description: errorMessage.value,
        };
      }

      if (hasGeneratedDraft.value) {
        return {
          kind: "draft",
          title: "Borrador generado",
          description: "El cierre quedo guardado como borrador. El periodo sigue abierto.",
        };
      }

      if (hasClosedClosure.value) {
        return {
          kind: "closed",
          title: "Cierre definitivo confirmado",
          description: closedAt.value
            ? `Cerrado el ${closedAtLabel.value}.`
            : "El periodo quedo cerrado definitivamente.",
        };
      }

      if (preview.value) {
        return {
          kind: "preview",
          title: "Preview generado",
          description: "Los datos estan calculados y listos para guardar como borrador.",
        };
      }

      return {
        kind: "empty",
        title: "Sin preview",
        description: "Selecciona un periodo y ejecuta el preview para comenzar.",
      };
    });
    const historyMessage = computed(() => {
      if (hasGeneratedDraft.value) {
        return "Podes continuar revisando y cerrar definitivamente este periodo.";
      }

      if (hasClosedClosure.value) {
        return "Este periodo ya fue cerrado.";
      }

      return "Se cargaron los datos guardados para continuar el flujo.";
    });
    const currentStep = computed(
      () => steps.find((step) => step.id === selectedStep.value) || steps[0]
    );

    const periodLabel = computed(() => {
      const monthName =
        monthOptions.find((item) => Number(item.id) === Number(form.month))?.name ||
        "Periodo";
      return `${monthName} ${form.year}`;
    });

    const incomeRows = computed(() =>
      normalizeSourceArray(preview.value, ["incomes", "data.incomes"]).map(
        (item, index) => ({
          id: getValue(item, "id", index),
          number: text(getValue(item, ["number", "receiptNumber"])),
          apartment: text(getValue(item, ["apartment.number", "apartmentNumber"])),
          account: text(getValue(item, ["account.name", "accountName"])),
          date: dateText(getValue(item, "date")),
          paymentMethod: text(
            getValue(item, ["paymentMethod.name", "paymentMethodName"])
          ),
          amount: money(getValue(item, "amount", 0)),
          currency: text(getValue(item, ["currency.code", "currency.name", "currency"])),
        })
      )
    );

    const fineRows = computed(() =>
      normalizeSourceArray(preview.value, ["fines", "data.fines"]).map(
        (item, index) => ({
          id: getValue(item, "id", index),
          apartment: text(getValue(item, ["apartment.number", "apartmentNumber"])),
          concept: text(getValue(item, ["concept.name", "conceptName", "detail"])),
          date: dateText(getValue(item, "date")),
          amount: money(getValue(item, "amount", 0)),
          currency: text(getValue(item, ["currency.code", "currency.name", "currency"])),
        })
      )
    );

    const expenseRows = computed(() =>
      normalizeSourceArray(preview.value, ["expenses", "data.expenses"]).map(
        (item, index) => ({
          id: getValue(item, "id", index),
          number: text(getValue(item, ["number", "receiptNumber"])),
          supplier: text(getValue(item, ["supplier.name", "supplierName"])),
          account: text(getValue(item, ["account.name", "accountName"])),
          date: dateText(getValue(item, "date")),
          description: text(getValue(item, ["description", "comment", "detail"])),
          amount: money(getValue(item, "amount", 0)),
          currency: text(getValue(item, ["currency.code", "currency.name", "currency"])),
        })
      )
    );

    const accountRows = computed(() =>
      (preview.value?.accountSummary || []).map((item, index) => ({
        id: getValue(item, ["accountId", "id"], index),
        account: text(getValue(item, ["accountName", "name", "account.name"])),
        initialBalance: money(getValue(item, "initialBalance", 0)),
        incomeAmount: money(getValue(item, "incomeAmount", 0)),
        expenseAmount: money(getValue(item, "expenseAmount", 0)),
        fineAmount: money(getValue(item, "fineAmount", 0)),
        finalBalance: money(getValue(item, "finalBalance", 0)),
      }))
    );

    const apartmentRows = computed(() =>
      (preview.value?.apartments || []).map((item, index) => ({
        id: getValue(item, ["apartmentId", "id"], index),
        apartment: text(getValue(item, ["apartmentNumber", "number"])),
        tower: text(getValue(item, ["towerName", "tower.name"])),
        owner: text(getValue(item, ["owner.name", "ownerName"])),
        resident: text(getValue(item, ["resident.name", "residentName"])),
        apportionment: text(getValue(item, "apportionment", 0)),
        previousBalance: money(getValue(item, "previousBalance", 0)),
        paymentsAmount: money(getValue(item, "paymentsAmount", 0)),
        commonExpenseAmount: money(getValue(item, "commonExpenseAmount", 0)),
        reserveFundAmount: money(getValue(item, "reserveFundAmount", 0)),
        finesAmount: money(getValue(item, "finesAmount", 0)),
        totalToPay: money(getValue(item, "totalToPay", 0)),
        concepts: (item.concepts || []).map((concept, conceptIndex) => ({
          id: getValue(concept, ["sourceId", "id"], `${index}-${conceptIndex}`),
          type: text(getValue(concept, "type")),
          description: text(getValue(concept, "description")),
          chargedTo: text(getValue(concept, "chargedTo")),
          amount: money(getValue(concept, "amount", 0)),
        })),
      }))
    );

    const summaryItems = computed(() => [
      { label: "Total ingresos", value: money(totals.value.totalIncome) },
      { label: "Total egresos", value: money(totals.value.totalExpenses) },
      { label: "Total multas", value: money(totals.value.totalFines) },
      { label: "Gasto comun", value: money(totals.value.totalCommonExpenses) },
      { label: "Fondo reserva", value: money(totals.value.totalReserveFund) },
      { label: "Total a cobrar", value: money(totals.value.totalToPay) },
      {
        label: "Apartamentos",
        value: totals.value.totalApartments || preview.value?.apartments?.length || 0,
      },
      { label: "Warnings", value: warnings.value.length },
    ]);

    const calculationItems = computed(() => {
      const snapshot = preview.value?.configurationSnapshot || {};
      return [
        {
          label: "Tipo de cierre",
          value: text(snapshot.closingTypeSnapshot),
        },
        {
          label: "Tipo fondo reserva",
          value: text(snapshot.reserveFundTypeSnapshot),
        },
        {
          label: "Valor fondo reserva",
          value: money(snapshot.reserveFundValueSnapshot),
        },
        {
          label: "Porcentaje fondo",
          value: `${number(snapshot.reserveFundPercentageSnapshot)}%`,
        },
        {
          label: "Gasto comun generado",
          value: money(totals.value.totalCommonExpenses),
        },
        {
          label: "Fondo reserva generado",
          value: money(totals.value.totalReserveFund),
        },
        {
          label: "Total a cobrar",
          value: money(totals.value.totalToPay),
        },
        {
          label: "Apartamentos",
          value: totals.value.totalApartments || 0,
        },
        {
          label: "Coeficiente total",
          value: text(totals.value.totalApportionment),
        },
      ];
    });

    const buildingSummaryItems = computed(() => [
      { label: "Total ingresos", value: money(totals.value.totalIncome) },
      { label: "Total egresos", value: money(totals.value.totalExpenses) },
      { label: "Total multas", value: money(totals.value.totalFines) },
      { label: "Total gasto comun", value: money(totals.value.totalCommonExpenses) },
      { label: "Total fondo reserva", value: money(totals.value.totalReserveFund) },
      { label: "Total a cobrar", value: money(totals.value.totalToPay) },
      { label: "Apartamentos", value: totals.value.totalApartments || 0 },
      { label: "Warnings", value: warnings.value.length },
    ]);

    const documentRows = computed(() => {
      const source =
        store.state.monthlyClosure.documents.length
          ? store.state.monthlyClosure.documents
          : currentClosure.value?.documents || [];

      return normalizeDocumentList(source).map((document, index) => {
        const metadata = document.metadata || {};
        const role = documentRole(document);
        const apartmentNumber = text(
          getValue(document, ["apartmentNumber", "metadata.apartmentNumber"], null)
        );
        const towerName = text(
          getValue(document, ["towerName", "metadata.towerName"], null)
        );

        return {
          ...document,
          id: getValue(document, "id", `${role}-${index}`),
          documentRole: role,
          apartmentId: getValue(document, ["apartmentId", "metadata.apartmentId"], null),
          apartmentNumber,
          towerName,
          typeLabel:
            role === "building_summary"
              ? "Resumen del edificio"
              : "Liquidacion individual",
          fileUrl: getValue(document, ["fileUrl", "url", "path", "metadata.fileUrl"], ""),
          createdAt: getValue(document, ["createdAt", "generatedAt"], metadata.generatedAt),
        };
      });
    });

    const buildingDocument = computed(
      () =>
        documentRows.value.find(
          (document) => document.documentRole === "building_summary"
        ) || null
    );
    const apartmentDocumentRows = computed(() =>
      documentRows.value.filter(
        (document) =>
          document.documentRole === "apartment_liquidation" ||
          document.documentRole === "unknown"
      )
    );
    const canGenerateDocuments = computed(
      () =>
        Boolean(closureId.value) &&
        hasClosedClosure.value &&
        !isLoading.value &&
        !generatingDocuments.value
    );
    const documentsButtonLabel = computed(() =>
      generatingDocuments.value ? "Generando PDFs..." : "Generar PDFs"
    );
    const documentStatusText = computed(() => {
      if (!closureId.value) return "Genera un borrador para habilitar documentos.";
      if (!hasClosedClosure.value) {
        return "Para generar documentos primero tenes que cerrar definitivamente el mes.";
      }
      if (!documentRows.value.length) {
        return "Todavia no se generaron documentos para este cierre.";
      }
      return `Documentos generados: ${documentRows.value.length}`;
    });

    const sendResult = computed(() => store.state.monthlyClosure.sendResult);
    const sendSummary = computed(() => {
      const summary = sendResult.value?.summary || {};
      return {
        sent: number(summary.sent ?? sendResult.value?.sentCount ?? sentRows.value.length),
        failed: number(
          summary.failed ?? sendResult.value?.failedCount ?? failedRows.value.length
        ),
        skipped: number(
          summary.skipped ?? sendResult.value?.skippedCount ?? skippedRows.value.length
        ),
      };
    });
    const sendWarnings = computed(() => sendResult.value?.warnings || []);
    const formatSendRow = (item, index, type) => ({
      id: getValue(item, ["id", "apartmentId"], `${type}-${index}`),
      apartment: text(
        getValue(item, [
          "apartmentNumber",
          "apartment.number",
          "document.apartmentNumber",
          "metadata.apartmentNumber",
        ])
      ),
      recipient: text(
        getValue(item, ["recipient", "recipientName", "name", "ownerName", "residentName"])
      ),
      recipientType: text(
        getValue(item, ["recipientType", "type", "recipientRole", "role"])
      ),
      email: text(getValue(item, ["email", "recipientEmail", "to"])),
      reason: text(getValue(item, ["reason", "error", "message", "detail"])),
      fileUrl: getValue(item, ["fileUrl", "document.fileUrl", "metadata.fileUrl"], ""),
    });
    const sentRows = computed(() =>
      normalizeResultArray(sendResult.value, ["sent", "data.sent"]).map((item, index) =>
        formatSendRow(item, index, "sent")
      )
    );
    const failedRows = computed(() =>
      normalizeResultArray(sendResult.value, ["failed", "data.failed"]).map((item, index) =>
        formatSendRow(item, index, "failed")
      )
    );
    const skippedRows = computed(() =>
      normalizeResultArray(sendResult.value, ["skipped", "data.skipped"]).map(
        (item, index) => formatSendRow(item, index, "skipped")
      )
    );
    const canSendDocuments = computed(
      () =>
        Boolean(closureId.value) &&
        hasClosedClosure.value &&
        Boolean(apartmentDocumentRows.value.length) &&
        !isLoading.value &&
        !sendingDocuments.value
    );
    const sendButtonLabel = computed(() =>
      sendingDocuments.value ? "Enviando documentos..." : "Enviar documentos"
    );
    const sendStatusText = computed(() => {
      if (!closureId.value) return "Genera y cierra un cierre para habilitar envios.";
      if (!hasClosedClosure.value) return "El cierre debe estar cerrado para enviar mails.";
      if (!apartmentDocumentRows.value.length) {
        return "Primero tenes que generar los PDFs del cierre.";
      }
      if (sendResult.value) {
        return `Resultado: ${sendSummary.value.sent} enviados, ${sendSummary.value.failed} fallidos, ${sendSummary.value.skipped} omitidos.`;
      }
      return `PDFs individuales disponibles: ${apartmentDocumentRows.value.length}`;
    });

    const incomeColumns = [
      { field: "number", header: "Numero" },
      { field: "apartment", header: "Apartamento" },
      { field: "account", header: "Cuenta" },
      { field: "date", header: "Fecha" },
      { field: "paymentMethod", header: "Forma de pago" },
      { field: "amount", header: "Monto", align: "right" },
      { field: "currency", header: "Moneda" },
    ];
    const fineColumns = [
      { field: "apartment", header: "Apartamento" },
      { field: "concept", header: "Concepto" },
      { field: "date", header: "Fecha" },
      { field: "amount", header: "Monto", align: "right" },
      { field: "currency", header: "Moneda" },
    ];
    const expenseColumns = [
      { field: "number", header: "Numero" },
      { field: "supplier", header: "Proveedor" },
      { field: "account", header: "Cuenta" },
      { field: "date", header: "Fecha" },
      { field: "description", header: "Descripcion" },
      { field: "amount", header: "Monto", align: "right" },
      { field: "currency", header: "Moneda" },
    ];
    const accountColumns = [
      { field: "account", header: "Cuenta" },
      { field: "initialBalance", header: "Saldo inicial", align: "right" },
      { field: "incomeAmount", header: "Ingresos", align: "right" },
      { field: "expenseAmount", header: "Egresos", align: "right" },
      { field: "fineAmount", header: "Multas", align: "right" },
      { field: "finalBalance", header: "Saldo final", align: "right" },
    ];
    const apartmentColumns = [
      { field: "apartment", header: "Apartamento" },
      { field: "tower", header: "Torre" },
      { field: "owner", header: "Propietario" },
      { field: "resident", header: "Residente" },
      { field: "apportionment", header: "Coeficiente" },
      { field: "previousBalance", header: "Saldo anterior", align: "right" },
      { field: "paymentsAmount", header: "Pagos", align: "right" },
      { field: "commonExpenseAmount", header: "Gasto comun", align: "right" },
      { field: "reserveFundAmount", header: "Fondo reserva", align: "right" },
      { field: "finesAmount", header: "Multas", align: "right" },
      { field: "totalToPay", header: "Total a pagar", align: "right" },
    ];
    const conceptColumns = [
      { field: "type", header: "Tipo" },
      { field: "description", header: "Descripcion" },
      { field: "chargedTo", header: "Cargado a" },
      { field: "amount", header: "Monto", align: "right" },
    ];

    const notify = (severity, detail) => {
      if (!store.state.toast?.add || !detail) return;
      store.state.toast.add({ severity, summary: "", detail, life: 5000 });
    };

    const validateForm = () => {
      if (!buildingId.value) return "Debe seleccionar un edificio.";
      if (!form.month) return "Debe seleccionar un mes.";
      if (!form.year || Number(form.year) < 2000) return "Debe ingresar un anio valido.";
      return null;
    };

    const onPreview = async () => {
      const validationMessage = validateForm();
      if (validationMessage) {
        store.commit("setMonthlyClosureError", validationMessage);
        notify("warn", validationMessage);
        return;
      }

      selectedApartment.value = null;
      const payload = {
        buildingId: Number(buildingId.value),
        month: Number(form.month),
        year: Number(form.year),
      };

      store.dispatch(actions.monthlyClosureActions.setSelectedPeriod, payload);
      const response = await store.dispatch(
        actions.monthlyClosureActions.preview,
        payload
      );

      if (response?.ok) {
        selectedStep.value = 2;
        notify("success", "Preview generado correctamente.");
        return;
      }

      notify("error", response?.message || "No fue posible generar el preview.");
    };

    const onGenerateDraft = async () => {
      if (!preview.value) {
        const message = "Debe ejecutar el preview antes de generar el borrador.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      if (hasGeneratedDraft.value) return;

      const validationMessage = validateForm();
      if (validationMessage) {
        store.commit("setMonthlyClosureError", validationMessage);
        notify("warn", validationMessage);
        return;
      }

      const confirmed = window.confirm(
        "Generar borrador de cierre\n\nSe guardara un borrador del cierre mensual con los datos revisados. Esto no cerrara el periodo ni bloqueara movimientos todavia."
      );
      if (!confirmed) return;

      const payload = {
        buildingId: Number(buildingId.value),
        month: Number(form.month),
        year: Number(form.year),
      };

      generatingDraft.value = true;
      let response;
      try {
        response = await store.dispatch(actions.monthlyClosureActions.generate, payload);
      } finally {
        generatingDraft.value = false;
      }

      if (response?.ok) {
        const id = response.closureId || response.data?.id || closureId.value;
        notify("success", `Borrador generado correctamente.${id ? ` ID: ${id}` : ""}`);
        return;
      }

      notify("error", response?.message || "No fue posible generar el borrador.");
    };

    const onCloseClosure = async () => {
      if (!closureId.value) {
        const message = "Debe generar un borrador antes de cerrar el mes.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      if (!hasGeneratedDraft.value) {
        const message = "Solo se puede cerrar definitivamente un cierre en estado borrador.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      const confirmed = window.confirm(
        "Vas a cerrar definitivamente este periodo. Luego no se podran modificar ingresos, egresos ni multas de este mes, y se actualizara la deuda de los apartamentos. Confirmas el cierre definitivo?"
      );
      if (!confirmed) return;

      closingClosure.value = true;
      let response;
      try {
        response = await store.dispatch(
          actions.monthlyClosureActions.close,
          closureId.value
        );
      } finally {
        closingClosure.value = false;
      }

      if (response?.ok) {
        notify("success", "Cierre definitivo confirmado correctamente.");
        return;
      }

      notify("error", response?.message || "No fue posible cerrar el mes.");
    };

    const onGenerateDocuments = async () => {
      if (!closureId.value) {
        const message = "Debe existir un cierre para generar documentos.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      if (!hasClosedClosure.value) {
        const message =
          "Para generar documentos primero tenes que cerrar definitivamente el mes.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      const confirmed = window.confirm(
        "Se generaran los PDFs del cierre mensual confirmado. Si ya existian documentos, pueden ser reemplazados por una nueva version. Queres continuar?"
      );
      if (!confirmed) return;

      generatingDocuments.value = true;
      let response;
      try {
        response = await store.dispatch(
          actions.monthlyClosureActions.generateDocuments,
          closureId.value
        );
        if (response?.ok) {
          await store.dispatch(actions.monthlyClosureActions.fetchOne, closureId.value);
        }
      } finally {
        generatingDocuments.value = false;
      }

      if (response?.ok) {
        notify("success", "PDFs generados correctamente.");
        return;
      }

      notify("error", response?.message || "No fue posible generar los PDFs.");
    };

    const onSendDocuments = async () => {
      if (!closureId.value) {
        const message = "Debe existir un cierre para enviar documentos.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      if (!hasClosedClosure.value) {
        const message = "El cierre debe estar cerrado para enviar documentos.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      if (!apartmentDocumentRows.value.length) {
        const message = "Primero tenes que generar los PDFs del cierre.";
        store.commit("setMonthlyClosureError", message);
        notify("warn", message);
        return;
      }

      const confirmed = window.confirm(
        "Se enviaran por mail las liquidaciones individuales generadas para este cierre. Si ya fueron enviadas antes, podrian volver a enviarse. Queres continuar?"
      );
      if (!confirmed) return;

      sendingDocuments.value = true;
      let response;
      try {
        response = await store.dispatch(
          actions.monthlyClosureActions.sendDocuments,
          closureId.value
        );
      } finally {
        sendingDocuments.value = false;
      }

      if (response?.ok) {
        notify("success", "Envio de documentos finalizado.");
        return;
      }

      notify("error", response?.message || "No fue posible enviar los documentos.");
    };

    const loadExistingClosure = async (id) => {
      store.dispatch(actions.monthlyClosureActions.clearPreview);
      const response = await store.dispatch(actions.monthlyClosureActions.fetchOne, id);
      if (!response?.ok) {
        notify("error", response?.message || "No fue posible cargar el cierre mensual.");
        return;
      }

      const loadedPreview = store.state.monthlyClosure.preview;
      const period = loadedPreview?.period || {};
      form.month = Number(period.month || loadedPreview?.month || form.month);
      form.year = Number(period.year || loadedPreview?.year || form.year);
      store.dispatch(actions.monthlyClosureActions.setSelectedPeriod, {
        buildingId: loadedPreview?.buildingId || buildingId.value,
        month: Number(form.month),
        year: Number(form.year),
      });
      selectedApartment.value = null;
      selectedStep.value = 2;
      notify("success", "Cierre cargado desde historial.");
    };

    const goHistory = () => {
      router.push("/contabilidad/cierres-mensuales");
    };

    const isStepDisabled = (stepId) => {
      if (stepId === 1) return false;
      if (stepId === 9) return !closureId.value;
      if (stepId === 10) return !closureId.value || !hasClosedClosure.value;
      return !preview.value;
    };

    const selectStep = (stepId) => {
      if (isStepDisabled(stepId)) return;
      selectedStep.value = stepId;
    };

    const nextStep = () => {
      if (selectedStep.value >= steps.length) return;
      const next = selectedStep.value + 1;
      if (isStepDisabled(next)) return;
      selectedStep.value = next;
    };

    const previousStep = () => {
      if (selectedStep.value <= 1) return;
      selectedStep.value -= 1;
    };

    const selectApartment = (row) => {
      selectedApartment.value = row;
    };

    onBeforeMount(async () => {
      if (routeClosureId.value) {
        await loadExistingClosure(routeClosureId.value);
      } else {
        store.dispatch(actions.monthlyClosureActions.clearPreview);
      }
      const exists = store.state.general.breadcrumbs.find(
        (item) => item.to === route.path
      );
      if (!exists) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    });

    return {
      form,
      months: monthOptions,
      steps,
      selectedStep,
      currentStep,
      buildingId,
      preview,
      warnings,
      isLoading,
      errorMessage,
      loadedFromHistory,
      historyMessage,
      closureId,
      closedAtLabel,
      hasGeneratedDraft,
      hasClosedClosure,
      canGenerateDraft,
      canShowCloseButton,
      canCloseClosure,
      canGenerateDocuments,
      canSendDocuments,
      generateButtonLabel,
      closeButtonLabel,
      documentsButtonLabel,
      sendButtonLabel,
      statusCard,
      documentStatusText,
      sendStatusText,
      periodLabel,
      totals,
      incomeRows,
      fineRows,
      expenseRows,
      accountRows,
      apartmentRows,
      selectedApartment,
      summaryItems,
      calculationItems,
      buildingSummaryItems,
      documentRows,
      buildingDocument,
      apartmentDocumentRows,
      sendResult,
      sendSummary,
      sendWarnings,
      sentRows,
      failedRows,
      skippedRows,
      incomeColumns,
      fineColumns,
      expenseColumns,
      accountColumns,
      apartmentColumns,
      conceptColumns,
      money,
      dateText,
      onPreview,
      onGenerateDraft,
      onCloseClosure,
      onGenerateDocuments,
      onSendDocuments,
      goHistory,
      isStepDisabled,
      selectStep,
      nextStep,
      previousStep,
      selectApartment,
    };
  },
};
</script>

<style scoped>
.monthly-closure {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 1.5rem 4rem;
}

.monthly-closure__header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.monthly-closure__steps {
  align-self: center;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  width: min(1040px, 100%);
}

.monthly-closure__step {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  color: var(--primary-dark);
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  min-height: 48px;
  padding: 0.65rem 0.75rem;
  text-align: left;
}

.monthly-closure__step span {
  align-items: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  display: flex;
  flex: 0 0 auto;
  height: 1.75rem;
  justify-content: center;
  width: 1.75rem;
}

.monthly-closure__step p {
  margin: 0;
}

.monthly-closure__step--active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.monthly-closure__step--disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

.monthly-closure__panel,
.monthly-closure__global-warning,
.monthly-closure__status {
  align-self: center;
  width: min(1120px, 100%);
}

.monthly-closure__panel,
.monthly-closure__summary-inline {
  background: #fff;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(35, 141, 130, 0.08);
  padding: 1.5rem;
}

.monthly-closure__summary-inline {
  margin-top: 1.5rem;
}

.monthly-closure__form {
  align-items: end;
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: minmax(180px, 1fr) minmax(160px, 1fr) auto;
}

.monthly-closure__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.monthly-closure__label {
  color: var(--primary-dark);
  font-family: "klavika";
  font-size: 0.9rem;
}

.monthly-closure__control {
  min-height: 48px;
  width: 100%;
}

.monthly-closure__button {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  box-shadow: none !important;
  min-height: 48px;
}

.monthly-closure__button--secondary {
  background-color: #fff !important;
  border-color: var(--primary) !important;
  color: var(--primary) !important;
}

.monthly-closure__loading {
  align-items: center;
  color: var(--primary-dark);
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.monthly-closure__loading :deep(.p-progress-spinner) {
  height: 2rem;
  width: 2rem;
}

.monthly-closure__message {
  border-radius: 8px;
  margin-top: 1rem;
  padding: 1rem;
}

.monthly-closure__message--error {
  background: #fee2e2;
  color: #991b1b;
}

.monthly-closure__message--warn {
  background: #fff7ed;
  color: #9a3412;
}

.monthly-closure__message ul {
  margin: 0.75rem 0 0;
  padding-left: 1.25rem;
}

.monthly-closure__message--flat {
  margin-top: 0;
}

.monthly-closure__status {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}

.monthly-closure__status span {
  color: #64748b;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.monthly-closure__status strong {
  color: var(--primary-dark);
}

.monthly-closure__status p {
  color: #64748b;
  margin: 0.25rem 0 0;
}

.monthly-closure__status--draft {
  background: #ecfdf5;
  border-color: #86efac;
}

.monthly-closure__status--closed {
  background: #eef2ff;
  border-color: #a5b4fc;
}

.monthly-closure__status--preview {
  background: #f0fdfa;
  border-color: #99f6e4;
}

.monthly-closure__status--error {
  background: #fee2e2;
  border-color: #fecaca;
}

.monthly-closure__status-id {
  white-space: nowrap;
}

.monthly-closure__history-note {
  align-items: center;
  align-self: center;
  background: #f8fafc;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  width: min(1120px, 100%);
}

.monthly-closure__history-note strong {
  color: var(--primary-dark);
}

.monthly-closure__history-note p {
  color: #64748b;
  margin: 0.25rem 0 0;
}

.monthly-closure__history-note button {
  background: transparent;
  border: none;
  color: var(--primary);
  cursor: pointer;
  font-weight: 600;
}

.monthly-closure__success {
  background: #ecfdf5;
  border: 1px solid #86efac;
  border-radius: 8px;
  color: #166534;
  margin-top: 1rem;
  padding: 1rem;
}

.monthly-closure__review-header,
.monthly-closure__summary-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.monthly-closure__review-header h3,
.monthly-closure__review-header p,
.monthly-closure__summary-header h3,
.monthly-closure__summary-header p {
  margin: 0;
}

.monthly-closure__review-header h3,
.monthly-closure__summary-header h3 {
  color: var(--primary-dark);
}

.monthly-closure__review-header p,
.monthly-closure__summary-header p,
.monthly-closure__review-header strong {
  color: var(--primary);
}

.monthly-closure__cards {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.monthly-closure__card {
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 92px;
  padding: 1rem;
}

.monthly-closure__card span {
  color: #64748b;
  font-size: 0.9rem;
}

.monthly-closure__card strong {
  color: var(--primary-dark);
  font-size: 1.25rem;
}

.monthly-closure__table-wrap {
  overflow-x: auto;
}

.monthly-closure__table-wrap--compact {
  margin-top: 0.5rem;
}

.monthly-closure__table {
  background: #fff;
  border-collapse: collapse;
  min-width: 860px;
  width: 100%;
}

.monthly-closure__table th,
.monthly-closure__table td {
  font-family: "klavika";
  padding: 0.75rem;
  vertical-align: top;
}

.monthly-closure__table td {
  border-bottom: 1px solid #e2e8f0;
  color: gray;
}

.monthly-closure__table th {
  background: var(--primary-light);
  border: 2px solid var(--primary-light);
  color: var(--primary-dark);
  font-weight: 600;
  text-align: center;
}

.monthly-closure__table-cell--right {
  text-align: right !important;
}

.monthly-closure__table-row--clickable {
  cursor: pointer;
}

.monthly-closure__table-row--clickable:hover {
  background: #f8fafc;
}

.monthly-closure__review-table--clickable :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.monthly-closure__review-table--clickable :deep(.p-datatable-tbody > tr:hover) {
  background: #f8fafc !important;
}

.monthly-closure__empty {
  color: #64748b;
  margin: 1rem 0 0;
  text-align: center;
}

.monthly-closure__total-bar {
  align-items: center;
  background: #f8fafc;
  border-radius: 8px;
  color: var(--primary-dark);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
}

.monthly-closure__concepts {
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  margin-top: 1.5rem;
  padding: 1rem;
}

.monthly-closure__concepts-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.monthly-closure__concepts-header h4 {
  color: var(--primary-dark);
  margin: 0;
}

.monthly-closure__concepts-header button {
  background: transparent;
  border: none;
  color: var(--primary);
  cursor: pointer;
}

.monthly-closure__documents-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.monthly-closure__documents-header h4,
.monthly-closure__documents-header p,
.monthly-closure__document-section h4 {
  margin: 0;
}

.monthly-closure__documents-header h4,
.monthly-closure__document-section h4 {
  color: var(--primary-dark);
}

.monthly-closure__documents-header p {
  color: #64748b;
  margin-top: 0.25rem;
}

.monthly-closure__document-section {
  border-top: 1px solid #e2e8f0;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
}

.monthly-closure__document-card {
  align-items: center;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 1rem;
}

.monthly-closure__document-card p {
  color: #64748b;
  margin: 0.25rem 0 0;
}

.monthly-closure__document-card a,
.monthly-closure__table a {
  color: var(--primary);
  font-weight: 600;
}

.monthly-closure__table--documents {
  margin-top: 1rem;
}

.monthly-closure__send-summary {
  margin-top: 1rem;
}

.monthly-closure__nav {
  align-self: center;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  width: min(1120px, 100%);
}

.monthly-closure__primary-actions {
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.monthly-closure__button--danger {
  background-color: #b91c1c !important;
  border-color: #b91c1c !important;
}

.monthly-closure__draft-caption {
  align-self: center;
  color: #166534;
}

.monthly-closure__draft-caption--closed {
  color: #3730a3;
}

.monthly-closure__nav-spacer {
  flex: 1;
}

@media screen and (max-width: 900px) {
  .monthly-closure__steps {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .monthly-closure__form {
    grid-template-columns: 1fr;
  }

  .monthly-closure__review-header,
  .monthly-closure__summary-header,
  .monthly-closure__concepts-header,
  .monthly-closure__documents-header,
  .monthly-closure__document-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .monthly-closure__nav {
    flex-direction: column;
  }

  .monthly-closure__primary-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .monthly-closure__nav-spacer {
    display: none;
  }

  .monthly-closure__status {
    align-items: flex-start;
    flex-direction: column;
  }

  .monthly-closure__history-note {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
